import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  getDoc,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Question, QuestionFormData } from '../types/liveSessions';

const QUESTIONS_COLLECTION = 'questions';

export class LiveSessionService {
  // Subscribe to questions for a specific session
  static subscribeToQuestions(sessionId: number, callback: (questions: Question[]) => void) {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('sessionId', '==', sessionId),
      orderBy('votes', 'desc'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const questions: Question[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        questions.push({
          id: doc.id,
          sessionId: data.sessionId,
          userId: data.userId,
          userName: data.userName,
          question: data.question,
          votes: data.votes || 0,
          upvotedBy: data.upvotedBy || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        });
      });
      callback(questions);
    });
  }

  // Add a new question
  static async addQuestion(sessionId: number, userId: string, userName: string, questionData: QuestionFormData): Promise<string> {
    const docRef = await addDoc(collection(db, QUESTIONS_COLLECTION), {
      sessionId,
      userId,
      userName,
      question: questionData.question,
      votes: 0,
      upvotedBy: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  }

  // Upvote a question
  static async upvoteQuestion(questionId: string, userId: string): Promise<void> {
    const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
    const questionDoc = await getDoc(questionRef);
    
    if (!questionDoc.exists()) {
      throw new Error('Question not found');
    }

    const questionData = questionDoc.data();
    const upvotedBy = questionData.upvotedBy || [];
    const votes = questionData.votes || 0;

    if (upvotedBy.includes(userId)) {
      // Remove upvote
      await updateDoc(questionRef, {
        votes: votes - 1,
        upvotedBy: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Add upvote
      await updateDoc(questionRef, {
        votes: votes + 1,
        upvotedBy: arrayUnion(userId),
        updatedAt: serverTimestamp(),
      });
    }
  }

  // Delete a question (only by the user who created it)
  static async deleteQuestion(questionId: string, userId: string): Promise<void> {
    const questionRef = doc(db, QUESTIONS_COLLECTION, questionId);
    const questionDoc = await getDoc(questionRef);
    
    if (!questionDoc.exists()) {
      throw new Error('Question not found');
    }

    const questionData = questionDoc.data();
    if (questionData.userId !== userId) {
      throw new Error('You can only delete your own questions');
    }

    await deleteDoc(questionRef);
  }

  // Get questions for a session (one-time fetch)
  static async getQuestions(sessionId: number): Promise<Question[]> {
    const q = query(
      collection(db, QUESTIONS_COLLECTION),
      where('sessionId', '==', sessionId),
      orderBy('votes', 'desc'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const questions: Question[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      questions.push({
        id: doc.id,
        sessionId: data.sessionId,
        userId: data.userId,
        userName: data.userName,
        question: data.question,
        votes: data.votes || 0,
        upvotedBy: data.upvotedBy || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      });
    });

    return questions;
  }
} 